import { Box, Typography } from "@material-ui/core";
import Highcharts from "highcharts";
import { useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import LineChart from "src/components/ScoringChart/Line/LineChart";
import { beautifulTooltip } from "src/components/ScoringChart/tool-utils/BeautifulTooltip";
import { FigureTypography } from "src/components/Typography/TitleTypography";
import TitleTypography from "src/components/Typography/TitleTypography";
import OverviewMonitoringWrapper from "src/components/Wrapper/OverviewMonitoringWrapper";
import { MONITORING } from "src/config/constants";
import { useUserContext } from "src/context/config/userContext";
import {
  useUserMonitoringChartSelector,
  useUserMonitoringSelector,
} from "src/context/monitoring/userMonitoringContext";
import { numberWithCommas, toFixed } from "src/services/utils";

const getOption = () => ({
  chart: {
    height: 260,
  },
  xAxis: {
    type: "datetime",
    labels: {
      enabled: false,
    },
  },
  yAxis: {
    tickAmount: 6,
    labels: {
      enabled: false,
    },
  },
  ...beautifulTooltip((value) => `$${numberWithCommas(toFixed(value, 2))}`),
});

export default function AssetOverviewOverview() {
  const { role } = useUserContext();
  const { pool } = useParams();
  const { fetchAssetOverview } = useUserMonitoringSelector((state) => state.fetch);
  const assetOverview = useUserMonitoringChartSelector(MONITORING.USER.assetOverview, pool);

  useEffect(() => {
    fetchAssetOverview(pool, { number: 360, interval: 2 }, { active: 2, isCheckMetadata: false });
  }, [pool, fetchAssetOverview]);

  const { isLoading, series, amountOfBorrow, amountOfDeposit, date, check } = useMemo(() => {
    if (assetOverview) {
      const check = assetOverview.deposit.length > 0 && assetOverview.borrow.length > 0;
      return {
        isLoading: false,
        series: [
          { name: "Deposit", data: [].concat(assetOverview.deposit) },
          { name: "Borrow", data: [].concat(assetOverview.borrow) },
        ],
        amountOfBorrow: check ? assetOverview.borrow.slice(-1)[0][1] : 0,
        amountOfDeposit: check ? assetOverview.deposit.slice(-1)[0][1] : 0,
        date: check ? Highcharts.dateFormat("%b %e, %Y, %k:%M", assetOverview.borrow.slice(-1)[0][0]) : "",
        check: check,
      };
    }
    return {
      isLoading: true,
      series: [
        { name: "Deposit", data: [] },
        { name: "Borrow", data: [] },
      ],
      amountOfBorrow: 0,
      amountOfDeposit: 0,
      date: "",
      check: false,
    };
  }, [assetOverview]);

  return (
    <Box mt={2}>
      <OverviewMonitoringWrapper
        isLoading={isLoading}
        title="Asset Overview"
        overview={
          role === "admin"
            ? "The chart shows the deposit and borrow of a user in a lending pool."
            : "The chart shows your deposit and borrow in a lending pool."
        }
        to={`/user-monitoring/asset-overview/${pool}`}
      >
        {check ? (
          <Box>
            <Box display="flex" flexWrap="wrap">
              <FigureTypography
                value={`$${numberWithCommas(toFixed(amountOfDeposit, 2))}`}
                title="Amount of Deposit"
                style={{ marginRight: "1rem" }}
              />
              <FigureTypography
                value={`$${numberWithCommas(toFixed(amountOfBorrow, 2))}`}
                title="Amount of Borrow"
                index={1}
              />
            </Box>
            <Typography>{date}</Typography>
            <LineChart series={series} option={getOption()} />
          </Box>
        ) : role === "admin" ? (
          <Box display="flex" justifyContent="center" alignItems="center" height="100%">
            <TitleTypography textProps={{ variant: "h4" }} title="This account doesn't have any transactions" />
          </Box>
        ) : (
          <Box display="flex" justifyContent="center" alignItems="center" height="100%">
            <TitleTypography textProps={{ variant: "h4" }} title="You don't have any transactions" />
          </Box>
        )}
      </OverviewMonitoringWrapper>
    </Box>
  );
}
